import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Zap, Share2 } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: 'easeOut' },
  }),
};

const pillars = [
  {
    icon: Eye,
    title: 'Visual Impact',
    desc: 'A dancefloor that commands the room from the moment it illuminates. Vibrant, reactive LED panels transform any space into a spectacle that leaves guests breathless.',
    accent: '#8B5CF6',
    image: 'https://images.pexels.com/photos/7317332/pexels-photo-7317332.jpeg?auto=compress&w=800',
    span: 'lg:col-span-2 lg:row-span-2',
  },
  {
    icon: Zap,
    title: 'Interactive Energy',
    desc: 'Reactive to music, movement, and the energy of every moment. The floor reads the room and responds in real time.',
    accent: '#3B82F6',
    image: 'https://images.unsplash.com/photo-1772858028490-88aeb579ce61?w=800&q=80',
    span: 'lg:col-span-1',
  },
  {
    icon: Share2,
    title: 'Social Media Magnet',
    desc: "The centerpiece guests can't stop filming — or talking about. Engineered to go viral at every event.",
    accent: '#8B5CF6',
    image: 'https://images.unsplash.com/photo-1674634045551-e1fdca6a076f?w=800&q=80',
    span: 'lg:col-span-1',
  },
];

export const ExperienceOverview = () => (
  <section
    id="experience"
    data-testid="experience-section"
    className="relative py-28 lg:py-36 px-6 bg-[#0A0A0A] overflow-hidden"
  >
    {/* Subtle divider glow */}
    <div
      className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24"
      style={{ background: 'linear-gradient(to bottom, transparent, rgba(139,92,246,0.5), transparent)' }}
    />

    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-16 lg:mb-20">
        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-xs font-manrope tracking-[0.3em] uppercase text-luma-purple mb-4"
          data-testid="experience-label"
        >
          The Experience
        </motion.p>
        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={1}
          className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-white max-w-2xl"
          data-testid="experience-headline"
        >
          Where light, music,
          <br />
          <span className="gradient-text">and movement</span>
          <br />
          become one.
        </motion.h2>
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return (
            <motion.div
              key={pillar.title}
              data-testid={`pillar-card-${i}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i + 1}
              className={`relative overflow-hidden rounded-2xl glass-card card-hover ${pillar.span} ${
                i === 0 ? 'min-h-[420px]' : 'min-h-[260px]'
              }`}
              style={{ border: '1px solid rgba(255,255,255,0.06)' }}
            >
              {/* Background image */}
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={pillar.image}
                  alt={pillar.title}
                  className="w-full h-full object-cover img-hover-zoom opacity-25"
                />
              </div>
              {/* Gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.6) 50%, rgba(10,10,10,0.2) 100%)`,
                }}
              />
              {/* Border glow on hover */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none"
                style={{ boxShadow: `inset 0 0 0 1px ${pillar.accent}40` }}
              />

              {/* Content */}
              <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${pillar.accent}18`, border: `1px solid ${pillar.accent}30` }}
                >
                  <Icon size={20} style={{ color: pillar.accent }} />
                </div>
                <h3
                  className="font-outfit font-bold text-xl lg:text-2xl text-white mb-3 leading-tight tracking-tight"
                  data-testid={`pillar-title-${i}`}
                >
                  {pillar.title}
                </h3>
                <p className="font-manrope text-sm text-white/55 leading-relaxed">
                  {pillar.desc}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Tagline */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        custom={4}
        className="mt-16 text-center"
      >
        <p
          className="font-outfit font-light text-xl sm:text-2xl tracking-wide"
          style={{ color: 'rgba(255,255,255,0.35)' }}
        >
          Luma transforms ordinary spaces into{' '}
          <span style={{ color: 'rgba(139,92,246,0.85)' }}>immersive environments</span>
          {' '}using reactive LED flooring and lighting.
        </p>
      </motion.div>
    </div>
  </section>
);

export default ExperienceOverview;
